/**
 * <TutorStudentOverview> — l'onglet « Aperçu » de la fiche d'un élève (template).
 *
 * Répond d'abord à « que fais-je avec cet élève à la prochaine séance ? », dans l'ordre retenu par
 * Wajih (02/11) : la prochaine séance, « En bref », la dernière séance, ses cours avec le masteur.
 * Au large, deux colonnes ; à l'étroit, l'un sous l'autre. En bas, « Son dossier », replié.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { SettingsSection } from "../../molecules/SettingsSection"
import { TutorStudentDossierCard } from "../../organisms/TutorStudentDossierCard"
import { TutorStudentLastSessionCard } from "../../organisms/TutorStudentLastSessionCard"
import { nextSessionHasContent, TutorStudentNextSessionCard } from "../../organisms/TutorStudentNextSessionCard"
import { TutorStudentObjectivesCard } from "../../organisms/TutorStudentObjectivesCard"
import { type UITutorStudentProfile } from "../../../ports/primary/ui/types/tutorEleves"

/** Au-delà, deux colonnes : le seuil du legacy (700), lu sur la largeur de l'onglet. */
const OVERVIEW_COLUMNS_FROM = 700

export type TutorStudentOverviewProps = {
    profile: UITutorStudentProfile
    /** En live : « Pour ce live ». Défaut `false`. */
    live?: boolean
    /** Le brief arrive : sa carte et « En bref » montrent leur forme. */
    briefLoading?: boolean
    onOpenStudyProgram?: () => void
    onOpenReports?: () => void
    onOpenAssessments?: () => void
    /** Pour une story : le dossier ouvert. */
    defaultDossierExpanded?: boolean
    testID?: string
}

export function TutorStudentOverview(props: TutorStudentOverviewProps) {
    const {
        profile,
        live = false,
        briefLoading = false,
        onOpenStudyProgram,
        onOpenReports,
        onOpenAssessments,
        defaultDossierExpanded,
        testID,
    } = props
    const { onLayout, atLeast: wideEnough } = useWidthAtLeast(OVERVIEW_COLUMNS_FROM)
    const lastSession = profile.sessionBrief?.lastSession
    // Deux colonnes au large ET avec de quoi remplir celle de la séance (le brief qui arrive, sa prochaine séance ou la
    // dernière), comme l'ancienne fiche : sans, elle restait vide à côté des autres cartes — le brief d'un nouvel élève
    // n'a souvent ni l'une ni l'autre.
    const wide =
        wideEnough && (briefLoading || nextSessionHasContent(profile.sessionBrief ?? null) || lastSession != null)

    const next = (
        <TutorStudentNextSessionCard
            brief={profile.sessionBrief ?? null}
            live={live}
            loading={briefLoading}
            onOpenAssessments={onOpenAssessments}
        />
    )
    const summary = briefLoading ? (
        <SettingsSection title="En bref">
            <DelayedSkeleton accessibilityLabel="Chargement de la synthèse">
                <Stack gap="1">
                    <Skeleton textSize="text-md" />
                    <Skeleton textSize="text-md" width="60%" />
                </Stack>
            </DelayedSkeleton>
        </SettingsSection>
    ) : profile.profileSummary != null && profile.profileSummary !== "" ? (
        <SettingsSection title="En bref">
            <Text size="text-md">{profile.profileSummary}</Text>
        </SettingsSection>
    ) : null
    const last =
        lastSession != null ? (
            <TutorStudentLastSessionCard
                lastSession={lastSession}
                reportsCount={profile.student.liveReportsCount}
                onOpenReports={onOpenReports}
            />
        ) : null
    const courses = <TutorStudentObjectivesCard profile={profile} onOpenStudyProgram={onOpenStudyProgram} />

    return (
        <View onLayout={onLayout} testID={testID}>
            <Stack gap="4">
                {wide ? (
                    <Row gap="4" align="start">
                        <Stack gap="4" style={styles.main}>
                            {next}
                            {last}
                        </Stack>
                        <Stack gap="4" style={styles.side}>
                            {summary}
                            {courses}
                        </Stack>
                    </Row>
                ) : (
                    <>
                        {next}
                        {summary}
                        {last}
                        {courses}
                    </>
                )}
                <TutorStudentDossierCard profile={profile} defaultExpanded={defaultDossierExpanded} />
            </Stack>
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    main: { flex: 5, minWidth: 0 },
    side: { flex: 4, minWidth: 0 },
}))
